
<script context="module">

  const VideoElementPool =  ((pool) => ({
    /**
     * @returns {HTMLVideoElement}
     */
    lease () {
      if (pool.length) return pool.pop();
      return document.createElement('video');
    },
    /**
     * @param {HTMLVideoElement} video_element
     */
    release (video_element) {
      pool.push(video_element);
    }
  }))([]);

  /**
   * Svelte action for managing video elements with pooling.
   * @param {HTMLElement} node
   * @param {Object} props
   */
  const videoElement = (node, props) => {

    /** @type {HTMLVideoElement} */
    const video = VideoElementPool.lease();
    video.src = props.src;
    video.title = props.title;
    video.muted = true;
    /* Prevent any default poster flashing */
    video.poster = "data:image/gif;base64,R0lGODlhBQAFAIAAAAAAAP///yH5BAUAAAEALAAAAAAFAAUAAAIEhI+pWAA7"; // black 5x5
    video.setAttribute('muted', '');
    video.setAttribute('data-id', props.id);
    video.load();
    node.append(video);

    if (props.playing) video.play();

    /* Pass events to action initiating node */
    /** @type {Array<[string, EventListener]>} */
    const listeners = [
      'ended', 'error', 'canplay'
    ].map(
      type => {
        if (type == 'canplay')
          return [type, () => {
            video.muted = true;
            node.dispatchEvent(new CustomEvent(type));
          }];
        else return [type, () => node.dispatchEvent(new CustomEvent(type))];
      }
    );
    // @TODO send beforeEnded crossfade_offset amount before end to allow for smoother crossfades
    // listeners.push(['timeupdate', () => {
    //   if ( times++ == 10 ) {
    //     console.log("timeupdate", video.duration, video.currentTime);
    //   }
    // }])
    listeners.forEach(([type, fn]) => video.addEventListener(type, fn));

    return {
      update (props) {
        // console.log('update playing', props.playing);
        
        if (props.playing) video.play();
        else {
          requestAnimationFrame(() => {
            video.load();
            video.pause();
          });
        }
      },

      destroy () {
        /* Remove listeners */
        listeners.forEach(([type, fn]) => video.removeEventListener(type, fn));
        node.removeChild(video);
        VideoElementPool.release(video);
      }
    };
  };

</script>

<script>

  import { createEventDispatcher, tick } from 'svelte';

  const dispatch = createEventDispatcher();

  export let item;
  export let playing;
  // /** @type {number} */
  // export let current_time;
  /** @type {number} milliseconds */
  export let crossfade_offset;

  let title;
  let src;
  let id;

  ({
    url_blob: src,
    title,
    id
  } = item);
  let actionProps = { src, title, id, playing, crossfade_offset: crossfade_offset / 1000 };

  $: {
    if (playing) play();
    else stop();
  }

  export async function play () {
    await tick();
    // console.log(`Play video ${id}`);
    actionProps = { src, title, id, playing, crossfade_offset: crossfade_offset / 1000 };
  }

  export async function stop () {
    await tick();
    // console.log(`Stop video ${id}`);
    actionProps = { src, title, id, playing, crossfade_offset: crossfade_offset / 1000 };
  }

</script>

{#if src}
  <div class="video-container"
    on:error="{() => dispatch('media.error')}"
    on:ended="{() => dispatch('media.finished')}"
    on:canplay="{() => dispatch('media.ready')}"
    use:videoElement={actionProps}></div>
{/if}

<style>
  .video-container {
    /* These are needed to make small videos up scale */
    display: flex;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .video-container :global(video) {
    object-fit: contain;
    object-position: center;
    max-width: 100%;
    max-height: 100%;
    flex-grow: 1;
  }
</style>
